import React from 'react';
import PropTypes from 'prop-types';
import {utils, writeFile} from 'xlsx';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {isEmpty, extractWorkSheetData} from "../../globals/GlobalFunctions";
import {GlobalConstants} from "../../globals/GlobalConstants";

export interface Props {
    excelData: any;
}

//as we need to do comparisons for both new vs old and old vs new we need sets for both groups for the values we will be checking for
const oldSSNSet = new Set();
const newSSNSet = new Set();
const oldNameSet = new Set();
const newNameSet = new Set();

function MemberAuditer ({excelData}: Props) {
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    try{
        //Check for 2 sheets here
        if (!isEmpty(excelData) && excelData.Sheets.OldMembers !== undefined && excelData.Sheets.NewMembers !== undefined) {
            let oldSheetDataArray = extractWorkSheetData(excelData.Sheets.OldMembers);

            //getting our old member data
            let oldArray : any[] = oldSheetDataArray[0];
            let oldColumnRange : number = oldSheetDataArray[1];
            let oldRowRange : number = oldSheetDataArray[2];

            let newSheetDataArray: any[] = extractWorkSheetData(excelData.Sheets.NewMembers);

            //new member data
            let newArray : any[] = newSheetDataArray[0];
            let newColumnRange : number = newSheetDataArray[1];
            let newRowRange : number = newSheetDataArray[2];

            //index of comparison values we are looking for
            let oldSSNIndex = oldArray.indexOf('SSN');
            let newSSNIndex = newArray.indexOf('SSN');

            let oldFNIndex = oldArray.indexOf('First Name');
            let newFNIndex = newArray.indexOf('First Name');

            let oldLNIndex = oldArray.indexOf('Last Name');
            let newLNIndex = newArray.indexOf('Last Name');

            //will be used later when comparing the original members against the new members to find removals
            let oldMembers : any[] = [];

            //make headers for both lists based on the member info provided
            let removedMembers : any[] = [["REMOVED MEMBERS"], oldArray.slice(0, oldColumnRange)];
            let addedMembers : any[] = [["ADDED MEMBERS"], newArray.slice(0, newColumnRange)];

            //go through the original member data to populate sets and get the list of members we can later iterate through
            for (let i = 1; i < oldRowRange; i++) {
                //getting the individual member's info from the sheet
                let oldMemberArray: any[] = [];

                let rowMultiplier = oldColumnRange * i;

                oldMemberArray = oldArray.slice(rowMultiplier, rowMultiplier + oldColumnRange);

                //if SSN add it to set
                if (oldSSNIndex >= 0) {
                    oldSSNSet.add(oldMemberArray[oldSSNIndex].toString().replace(/-/g, "").trim());
                }

                //if names add to set
                if (oldFNIndex >= 0 && oldLNIndex >= 0) {
                    oldNameSet.add(oldMemberArray[oldFNIndex].toString().toUpperCase().trim() + oldMemberArray[oldLNIndex].toString().toUpperCase().trim());
                }

                //add old members to array so we dont have to iterate and get every member again later when we use the members to compare the other way
                oldMembers.push(oldMemberArray);
            }

            //go through the updated members
            for (let i = 1; i < newRowRange; i++) {

                let newMemberArray: any[] = [];

                //false until found, if false then they are added to added members array
                let isOnOld : boolean = false;

                let rowMultiplier = newColumnRange * i;

                //select each member
                newMemberArray = newArray.slice(rowMultiplier, rowMultiplier + newColumnRange);

                let newMemberSSN : string = "";
                let newMemberName : string = "";

                //add new member list info to set for removal comparison
                if (newSSNIndex >= 0) {
                    newMemberSSN = newMemberArray[newSSNIndex].toString().replace(/-/g, "").trim();
                    if(newMemberSSN.length > 0) {
                        newSSNSet.add(newMemberSSN);
                    }
                }

                if (newFNIndex >= 0 && newLNIndex >= 0) {
                    newMemberName = newMemberArray[newFNIndex].toString().toUpperCase().trim() + newMemberArray[newLNIndex].toString().toUpperCase().trim()
                    newNameSet.add(newMemberName);
                }

                //if the SSN is not empty and is found in the old set while not being a garbage default input or name is found dont add the member to the changes list
                if ((newMemberSSN.length > 1 && oldSSNSet.has(newMemberSSN) && !GlobalConstants.badSSNSet.has(newMemberSSN)) ||
                    (newMemberName.length > 1 && oldNameSet.has(newMemberName))) {
                    isOnOld  = true;
                }

                //added members
                if(!isOnOld){
                    addedMembers.push(newMemberArray);
                }
            }

            //using the old list we got earlier we check it against the new member list to find removals, same as above but reversed
            oldMembers.forEach(member => {
                let isInNew : boolean = false;
                let oldMemberSSN = oldSSNIndex >= 0 ? member[oldSSNIndex].toString().replace(/-/g, "").trim() : "";
                let oldMemberName : string = oldFNIndex >= 0 && oldLNIndex >= 0 ? member[oldFNIndex].toString().toUpperCase().trim() +
                    member[oldLNIndex].toString().toUpperCase().trim() : "";
                if ((oldMemberSSN.length > 1 && newSSNSet.has(oldMemberSSN) && !GlobalConstants.badSSNSet.has(oldMemberSSN)) ||
                    (oldMemberName.length > 1 && newNameSet.has(oldMemberName))) {
                    isInNew = true;
                }
                if(!isInNew){
                    removedMembers.push(member);
                }
            });

            //add a space for the two lists will be combined
            removedMembers.push([""]);

            //combine into one list
            addedMembers.forEach(member => {
                removedMembers.push(member);
            });

            //new workbook 1
            const memberAuditWorkbook = utils.book_new();

            //create worksheet from our array of arrays
            let ws1 = utils.aoa_to_sheet(removedMembers);

            //add it to the new workbook
            utils.book_append_sheet(memberAuditWorkbook, ws1, "MemberAuditChangeList");

            if (memberAuditWorkbook && memberAuditWorkbook.SheetNames.includes("MemberAuditChangeList")){
                if(!memberAuditWorkbook.Props) memberAuditWorkbook.Props = {};

                //setting the workbook title through props
                memberAuditWorkbook.Props.Title = excelData.FileName + " ChangedMembersList.xlsx";

                //writing the file which will make the download, second param is the name of the file and third is required options
                writeFile(memberAuditWorkbook, excelData.FileName + " ChangedMembersList.xlsx",{});

                console.log("File Export Complete!");
                setSuccessAlert(true);
            }
            else {
                setErrorAlert(true);
                console.log("Unknown Export Error!");
            }

            //reset sets and sheets at the end
            oldSSNSet.clear();
            oldNameSet.clear();
            newSSNSet.clear();
            newNameSet.clear();

            excelData.Sheets.OldMembers = undefined;
            excelData.Sheets.NewMembers = undefined;
        }
    }
    catch(e) {
        console.log("ERROR", e);

        setErrorAlert(true);
        //clear it out after errors in case of rerendering otherwise the error spams will continue until morale improves
        oldSSNSet.clear();
        oldNameSet.clear();
        newSSNSet.clear();
        newNameSet.clear();

        excelData.Sheets.OldMembers = undefined;
        excelData.Sheets.NewMembers = undefined;
    }
    return (
        <div>
            {successAlert ? <Alert severity='success' onClose={() => setSuccessAlert(false)} variant="filled">
                <AlertTitle>Success!</AlertTitle>
                File Download Complete!
            </Alert> : <></> }
            {errorAlert ? <Alert severity='error' onClose={() => setErrorAlert(false)} variant="filled">
                <AlertTitle>Error!</AlertTitle>
                There was an error with this file!
            </Alert> : <></> }
        </div>
    );
}


export default React.memo(MemberAuditer)

MemberAuditer.propTypes = {
    excelData: PropTypes.object.isRequired,
};